import { useState } from 'react';
import { Link } from 'react-router-dom';
import { leadgenApi, type LeadGenEnrichment } from '@/api/leadgen.api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
import { usePermissions } from '@/hooks/usePermissions';

export function LeadGenEnrichmentPage() {
  const { hasAny } = usePermissions();
  const canManage = hasAny('LEAD_GEN_MANAGE');
  const [leadId, setLeadId] = useState('');
  const [email, setEmail] = useState('');
  const [enrichment, setEnrichment] = useState<LeadGenEnrichment | null>(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const id = Number(leadId);
    if (!id) return;
    setLoading(true);
    try {
      setEnrichment(await leadgenApi.getEnrichment(id));
    } catch (e) {
      setEnrichment(null);
      showApiError(e, 'No enrichment for this lead yet');
    } finally {
      setLoading(false);
    }
  };

  const enrichFromEmail = async () => {
    const id = Number(leadId);
    if (!id || !email.trim()) {
      notify.error('CRM lead ID and email required');
      return;
    }
    setLoading(true);
    try {
      const result = await leadgenApi.enrichFromEmail(id, email.trim());
      setEnrichment(result);
      notify.success('Enrichment applied from email domain');
    } catch (e) {
      showApiError(e, 'Enrichment failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-content">
      <PageHeader
        title="Lead enrichment"
        description="Domain inference and firmographic hints — runs automatically on form submit; manual refresh here"
        breadcrumbs={[
          { label: 'Org', to: '/org' },
          { label: 'Lead Gen', to: '/lead-gen' },
          { label: 'Enrichment' },
        ]}
      />

      <p className="text-sm text-slate-600">
        Uses the CRM lead ID from{' '}
        <Link to="/crm/leads" className="text-primary-600 hover:underline">
          CRM leads
        </Link>
        . Full Clearbit-class providers remain roadmap.
      </p>

      <div className="card p-4 grid gap-4 md:grid-cols-2 max-w-2xl">
        <Input label="CRM lead ID" value={leadId} onChange={(e) => setLeadId(e.target.value)} />
        <Input label="Email (for domain enrich)" value={email} onChange={(e) => setEmail(e.target.value)} />
        <div className="flex gap-2 md:col-span-2">
          <Button variant="secondary" loading={loading} onClick={() => void load()}>
            Load enrichment
          </Button>
          {canManage && (
          <Button loading={loading} onClick={() => void enrichFromEmail()}>
            Enrich from email
          </Button>
          )}
        </div>
      </div>

      {enrichment && (
        <div className="card p-4">
          <h3 className="font-semibold mb-3">Enrichment record</h3>
          <dl className="grid gap-2 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-slate-500">Source</dt>
              <dd>{enrichment.enrichmentSource ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-slate-500">Company</dt>
              <dd>{enrichment.companyName ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-slate-500">Industry</dt>
              <dd>{enrichment.industry ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-slate-500">Website</dt>
              <dd>{enrichment.website ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-slate-500">Country</dt>
              <dd>{enrichment.country ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-slate-500">City</dt>
              <dd>{enrichment.city ?? '—'}</dd>
            </div>
          </dl>
        </div>
      )}
    </div>
  );
}
